import { fromBase64Url, toBase64Url } from './encoding';

/**
 * Parameters for turning a password into the login key the server checks.
 * The client enforces these itself, so a hostile server cannot weaken them.
 * Changing any value is a new KDF version and needs a migration plan.
 */
export const LOGIN_KDF = {
  id: 'curentia-login-v1',
  iterations: 600_000,
  saltBytes: 16,
  keyBytes: 32,
  info: 'curentia/v1/login-key',
} as const;

/** A login key on the wire: 32 bytes as unpadded base64url. */
export const LOGIN_KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * Stretches a password with PBKDF2-SHA-256 and returns it as an HKDF base key.
 * The login key is one HKDF output. Phase 3 derives the vault wrapping key from
 * the same base with a different info label, so the server, which only sees the
 * login key, learns nothing about the vault key.
 */
export async function stretchPassword(password: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  if (password.length === 0) throw new Error('Password is empty');
  if (salt.length !== LOGIN_KDF.saltBytes) throw new Error('Login salt has the wrong length');
  const subtle = globalThis.crypto.subtle;
  const passwordKey = await subtle.importKey(
    'raw',
    new TextEncoder().encode(password.normalize('NFC')),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const stretched = await subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: LOGIN_KDF.iterations },
    passwordKey,
    256,
  );
  return subtle.importKey('raw', stretched, 'HKDF', false, ['deriveBits', 'deriveKey']);
}

/** The login key from an already stretched password. */
export async function loginKeyFrom(base: CryptoKey): Promise<string> {
  const bits = await globalThis.crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: new TextEncoder().encode(LOGIN_KDF.info),
    },
    base,
    LOGIN_KDF.keyBytes * 8,
  );
  return toBase64Url(new Uint8Array(bits));
}

/**
 * Derives the login key from a password and the salt returned by the server's
 * pre-login step. Returns base64url text ready to send. The server stores only
 * a scrypt hash of this value and never sees the password.
 */
export async function deriveLoginKey(password: string, salt: string): Promise<string> {
  return loginKeyFrom(await stretchPassword(password, fromBase64Url(salt)));
}

/**
 * What a registered device signs to set a new login key after the vault was
 * opened with the recovery code. Only a device holding its private signing
 * key, which lives inside the vault, can make this signature.
 */
export function loginResetBytes(accountId: string, deviceId: string, newLoginKey: string, signedAt: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(
    `curentia/v1/login-reset\0${accountId}\0${deviceId}\0${newLoginKey}\0${signedAt}`,
  ) as Uint8Array<ArrayBuffer>;
}
